import {
	ClientIntakeFormContext,
	ClientIntakeFormType,
	IntakeFormResponse,
} from '@models/client-intake';
import type {
	FieldId,
	FieldLabel,
	Form,
	FormField,
	FormSection,
	FormSubSection,
	FormValue,
} from '@models/client-intake';
import type {
	EngineFormItem,
	EngineFormItemFormField,
} from '@models/client-portal/engine-client-intake';
import { EngineFormItemType } from '@models/client-portal/engine-client-intake';
import type { FilteredResult } from '@domain/rule-definitions';
import { GeneralContext } from '@models/client-intake';
import { PortalEngineFullContext } from '../state/portal-engine.models';
import { isSet } from '@globalUtils/utilities/value';
import { EngineFormContent } from '../state/portal-engine.models';
import { ClientIntakeResponse } from '@domain/intake-form-client-responses';
import { BusinessUser } from '@models/businesses';
import {
	FormAnswer,
	FieldResponseStatus,
	MapToConversionType,
	FormDataTypes,
} from '@models/client-intake';
import {
	extractValueFromMapTo,
	getDisplayValue,
	DefaultValueContext,
} from './map-to.utils';

export type { DefaultValueContext } from './map-to.utils';

export function buildContextData(
	responses: IntakeFormResponse[],
	contextData: ClientIntakeFormContext,
): PortalEngineFullContext {
	const clients = contextData.clients?.all?.map((client) => {
		const clientResponse = responses.find(
			(r) => r.clientId === client.id || r.formId === client.id,
		);

		const intakeResponses: Record<string, unknown> = {};
		if (clientResponse?.formAnswers) {
			Object.entries(clientResponse.formAnswers).forEach(
				([questionId, answer]) => {
					intakeResponses[questionId] = answer.answer;
				},
			);
		}

		return {
			id: client.id,
			name: client.name,
			type: client.type,
			intakeResponses: {
				answers: intakeResponses,
			},
		};
	});

	const generalResponse = responses.find(
		(r) => r.id === contextData?.general?.id,
	);

	const generalIntakeAnswers: Record<string, unknown> = {};
	if (generalResponse?.formAnswers) {
		Object.entries(generalResponse.formAnswers).forEach(
			([questionId, answer]) => {
				generalIntakeAnswers[questionId] = answer.answer;
			},
		);
	}

	return {
		clients: {
			all: clients ?? [],
		},
		general: {
			intakeResponses: {
				answers: generalIntakeAnswers,
			},
			id: contextData.general?.id,
			hasPersonClients: contextData.general?.hasPersonClients,
			hasCorporationClients: contextData.general?.hasCorporationClients,
			name: contextData.general?.name,
			type: contextData.general?.type,
		},
	};
}

/**
 * Extracts form content from rules engine results
 * Converts the nested FilteredResult structure into a flat array of forms with their content
 */
export function getFormContent(
	engineResults: Array<FilteredResult<EngineFormItem>>,
): Array<EngineFormContent> {
	const formContent = [] as Array<EngineFormContent>;

	// Handle the extra nesting layer - the actual results are in the first item's children
	const engineContent =
		(engineResults[0]?.children as Array<FilteredResult<EngineFormItem>>) || [];

	engineContent.forEach((result) => {
		const form: {
			id: string;
			content: EngineFormItem[];
			formType: ClientIntakeFormType;
			name: string;
		} = {
			id: '',
			content: [],
			formType: ClientIntakeFormType.GENERAL,
			name: '',
		};

		if (result.context['client']) {
			form.id = (result.context['client'] as { id: string }).id;
			form.formType = ClientIntakeFormType.CLIENT;
			form.name = (result.context['client'] as { name: string }).name;
		} else {
			// If no client context, it's the general/matter form
			const general = result.context['general'] as GeneralContext;
			form.id = general.id;
			form.formType = ClientIntakeFormType.GENERAL;
			form.name = general.name;
		}

		getFormChildren(result.children, form);

		formContent.push(form);
	});

	return formContent;
}

/**
 * Populates dynamic options for form items by extracting context from template:option children
 * Creates a new array with modified content to avoid mutating frozen objects
 */
function populateDynamicOptions(
	child: FilteredResult<EngineFormItem>,
): EngineFormItem[] | undefined {
	const item = child.content?.[0]?.item as EngineFormItemFormField;

	if (!child.content || !item?.optionType || item.optionType !== 'dynamic') {
		return child.content;
	}

	const optionChildren = child.children.filter((child) =>
		child.keywords.include.includes('template:option'),
	);

	if (optionChildren.length > 0) {
		const contextLoop = optionChildren.map((child) => child.context);

		const formOptions = contextLoop.map((context: Record<string, unknown>) => {
			const client = context['client'] as { id: string; name?: string };

			return {
				id: client.id,
				value: client.id,
				label: client.name || client.id,
			};
		});

		return child.content.map((item) => ({
			...item,
			item: {
				...item.item,
				options: formOptions,
			},
		}));
	}

	return child.content;
}

/**
 * Generates multiple field instances from a template field by extracting context from template:field children
 * Creates one field instance per context item (e.g., one per client)
 * Supports dynamic labels with {{this.name}} interpolation
 */
function generateDynamicFields(
	child: FilteredResult<EngineFormItem>,
): EngineFormItem[] | undefined {
	if (!child.content?.[0]?.dynamicallyGenerated) {
		return child.content;
	}

	const fieldChildren = child.children.filter(
		(child) =>
			child.keywords.include.includes('template:field') ||
			child.keywords.include.includes('template:label'),
	);

	if (fieldChildren.length === 0) {
		return child.content;
	}

	const generatedFields: EngineFormItem[] = [];
	const templateField = child.content[0];

	fieldChildren.forEach((fieldChild) => {
		const context = fieldChild.context;
		const client = context['client'] as { id: string; name?: string };

		const uniqueFieldId = `${templateField.item.id}-${client.id}`;

		const item = templateField.item as EngineFormItemFormField;

		let fieldLabel = item.label;
		if (item.labelType === 'dynamic' && client.name) {
			fieldLabel = fieldLabel?.replace(
				/\{\{this\.name\}\}/g,
				client.name,
			) as FieldLabel; //todo replace with handlebars in engine reducer
		}

		generatedFields.push({
			...templateField,
			item: {
				...item,
				id: uniqueFieldId as FieldId, // not entirely true
				label: fieldLabel,
				mapTo: {
					collection: FormDataTypes.Matter,
					field: `capacity.shares.${client.id}`,
					conversionType: MapToConversionType.STRING_TO_NUMBER,
				},
			},
		});
	});

	return generatedFields;
}

/**
 * Recursively extracts form items from engine results
 */
export function getFormChildren(
	children: Array<FilteredResult<EngineFormItem>>,
	form: { id: string; content: EngineFormItem[] },
): void {
	children.forEach((child) => {
		if (child.content) {
			let processedContent = generateDynamicFields(child);

			if (processedContent) {
				processedContent = populateDynamicOptions({
					...child,
					content: processedContent,
				});
			}

			if (processedContent) {
				form.content.push(...processedContent);
			}
		}
		if (child.children) {
			getFormChildren(child.children, form);
		}
	});
}

/**
 * Orders and structures form content from engine results into Form objects
 * Builds the hierarchical structure of sections, subsections, and fields
 * @param formContent - The form content extracted from engine results
 * @param existingResponsesByForm - Optional map of form ID to response values for populating defaults
 */
export function orderFormContent(
	formContent: Array<{
		id: string;
		content: EngineFormItem[];
		formType: ClientIntakeFormType;
		name: string;
	}>,
	existingResponsesByForm?: Map<string, Map<string, FormValue>>,
): Form[] {
	return formContent.map((form) => {
		const sectionItems = form.content.filter(
			(item) =>
				item.type === EngineFormItemType.SECTION &&
				item.item.parentItemId === null,
		);

		sectionItems.sort((a, b) => (a.item.order ?? 0) - (b.item.order ?? 0));

		const sections: FormSection[] = sectionItems.map((item) => ({
			...(item.item as FormSection),
			fields: [],
			order: item.item.order, // Preserve engine order
		}));

		// Get existing responses for this specific form
		const formResponses = existingResponsesByForm?.get(form.id);

		sections.forEach((section) => {
			buildHierarchy(section, form.content, form.id, formResponses);
		});

		return {
			id: form.id,
			...(form.formType === ClientIntakeFormType.CLIENT && {
				clientId: form.id,
			}),
			...(form.formType === ClientIntakeFormType.GENERAL && {
				generalFormId: form.id,
			}),
			sections,
			formType: form.formType,
			name: form.name,
		};
	});
}

/**
 * Recursively builds the hierarchical structure of form sections, subsections, and fields
 * @param parent - The parent section or subsection
 * @param allItems - All form items from the engine
 * @param formId - The form ID used for prefixing field IDs
 * @param existingResponses - Optional map of field ID to existing response values (for populating defaults)
 */
export function buildHierarchy(
	parent: FormSection | FormSubSection,
	allItems: EngineFormItem[],
	formId: string,
	existingResponses?: Map<string, FormValue>,
): void {
	const parentId = parent.id;
	if (!parentId) return;

	const children = allItems.filter(
		(item) => item.item.parentItemId === parentId,
	);

	children.sort((a, b) => (a.item.order ?? 0) - (b.item.order ?? 0));

	children.forEach((child) => {
		if (child.type === EngineFormItemType.SUB_SECTION) {
			const subsection: FormSubSection = {
				...(child.item as FormSubSection),
				fields: [],
				order: child.item.order, // Preserve engine order
			};
			parent.fields.push(subsection);
			buildHierarchy(subsection, allItems, formId, existingResponses);
		} else if (child.type === EngineFormItemType.FIELD) {
			const fieldId = child.item.id as FieldId;
			const field = child.item as FormField;

			// Check if there's an existing response for this field (cast to FormValue)
			const existingValue = existingResponses?.get(fieldId);

			const defaultValue = isSet(existingValue) ? existingValue : undefined;

			const newField: FormField = {
				...field,
				id: fieldId,
				order: child.item.order, // Preserve engine order
			};

			if (isSet(defaultValue)) {
				newField.defaultValue = defaultValue;
			}

			parent.fields.push(newField);
		}
	});
}

/**
 * Populates default values on a flat array of EngineFormItems based on mapTo configuration.
 * This should be called BEFORE building the form hierarchy.
 *
 * @param formContent - The flat form content from the engine
 * @param context - The data context for extracting values
 * @returns A new array with defaultValue populated on field items
 */
export function populateDefaultValuesFlat(
	formContent: EngineFormContent,
	context: DefaultValueContext,
): EngineFormContent {
	const updatedContent = formContent.content.map((item) => {
		if (item.type !== EngineFormItemType.FIELD) {
			return item;
		}

		const engineField = item.item as EngineFormItemFormField;
		const mapTo = engineField.mapTo;

		if (!mapTo) {
			return item;
		}

		const defaultValue = extractValueFromMapTo(
			mapTo,
			context,
			engineField.options,
		);

		if (defaultValue === undefined) {
			return item;
		}

		return {
			...item,
			item: {
				...engineField,
				defaultValue,
			},
		};
	});

	return {
		...formContent,
		content: updatedContent,
	};
}

/**
 * Generate initial form responses from data context using mapTo configuration.
 * Uses the unified extractValueFromMapTo for consistent value extraction.
 *
 * @param form - The form content to generate responses for
 * @param context - The data context containing matter, client, etc.
 * @param loggedInUser - The user generating the responses
 * @param intakeFormId - The intake form ID
 * @returns A ClientIntakeResponse with formAnswers populated from the data context
 */
export function generateInitialFormResponses(
	form: EngineFormContent,
	context: DefaultValueContext,
	loggedInUser: BusinessUser,
	intakeFormId: string,
): ClientIntakeResponse {
	const formAnswers: Record<string, FormAnswer> = {};
	const now = Date.now();

	form.content.forEach((item) => {
		if (item.type !== EngineFormItemType.FIELD) return;

		const engineItem = item.item as EngineFormItemFormField;
		const mapTo = engineItem.mapTo;

		if (!mapTo) return;

		const value = extractValueFromMapTo(mapTo, context, engineItem.options);

		if (value !== undefined && value !== null && value !== '') {
			formAnswers[engineItem.id] = {
				questionId: engineItem.id,
				answer: value,
				displayValue: getDisplayValue(value, engineItem.options),
				status: FieldResponseStatus.ADDED_TO_MATTER,
				submittedAt: now,
				label: engineItem.label,
			};
		}
	});

	return {
		id: form.id,
		formId: intakeFormId,
		clientId: form.id,
		type: form.formType,
		submittedAt: now,
		submittedBy: loggedInUser?.id ?? '',
		updatedAt: now,
		formAnswers,
		formName: form.name,
	};
}

/**
 * Calculate form progress statistics
 * @param formResponses - The form responses to analyze
 * @param flatEngineForms - The flat engine forms with field definitions
 * @returns Progress stats including total fields, filled fields, and progress percentage
 */
export function calculateFormProgressStats(
	formResponses: ClientIntakeResponse[] | undefined,
	flatEngineForms: EngineFormContent[] | null,
): { total: number; filled: number; progress: number } {
	const DEFAULT_FORM_PROGRESS_STATS = {
		total: 0,
		filled: 0,
		progress: 0,
	};

	if (!formResponses || formResponses.length === 0) {
		return DEFAULT_FORM_PROGRESS_STATS;
	}

	if (!flatEngineForms || flatEngineForms.length === 0) {
		return DEFAULT_FORM_PROGRESS_STATS;
	}

	const flattenedResponses = formResponses.flatMap((response) =>
		Object.values(response.formAnswers),
	);

	const total = flatEngineForms.reduce((acc, form) => {
		return (
			acc +
			form.content.filter((item) => item.type === EngineFormItemType.FIELD)
				.length
		);
	}, 0);

	const filled = flattenedResponses?.filter(
		(answer) =>
			isSet(answer.answer) &&
			[
				FieldResponseStatus.READY_TO_REVIEW,
				FieldResponseStatus.ADDED_TO_MATTER,
				FieldResponseStatus.CUSTOM,
			].includes(answer.status),
	).length;

	const progress = total > 0 ? Math.round((filled / total) * 100) : 0;

	return {
		total,
		filled,
		progress,
	};
}
